import React, { useCallback, useEffect, useRef, useState } from "react";

// A still row of service cards that the visitor moves through on their own
// terms: the arrow buttons, the keyboard arrows (once the row has focus), or a
// swipe / trackpad scroll. Native scrolling does the moving, and scroll-snap
// lands each step on a card edge, so there is no transform maths to keep in sync.

function ArrowIcon({ direction }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {direction === "prev" ? (
        <>
          <path d="M19 12H5" />
          <path d="M11 18l-6-6 6-6" />
        </>
      ) : (
        <>
          <path d="M5 12h14" />
          <path d="M13 6l6 6-6 6" />
        </>
      )}
    </svg>
  );
}

// Distance from one card's left edge to the next: one card plus the gap.
function stepSize(el) {
  const [first, second] = el.children;
  return second ? second.offsetLeft - first.offsetLeft : el.clientWidth * 0.8;
}

function ServiceCarousel({ label, children }) {
  const trackRef = useRef(null);
  const [scroll, setScroll] = useState({
    atStart: true,
    atEnd: false,
    overflows: true,
    stops: 1,
    active: 0,
  });

  const measure = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;

    const max = el.scrollWidth - el.clientWidth;
    const x = el.scrollLeft;
    const step = stepSize(el);
    // One stop per card the row can start on; the last stop is "scrolled to
    // the end", which may land part-way through a card.
    const stops = max > 2 ? Math.ceil((max - 2) / step) + 1 : 1;
    const atEnd = x >= max - 2;

    setScroll({
      atStart: x <= 2,
      atEnd,
      overflows: max > 2,
      stops,
      active: atEnd ? stops - 1 : Math.min(stops - 1, Math.round(x / step)),
    });
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return undefined;

    measure();
    el.addEventListener("scroll", measure, { passive: true });
    // Watch the cards as well as the row: when cards change size (images,
    // fonts, breakpoints) the scroll width moves even if the row's box doesn't.
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    Array.from(el.children).forEach((card) => observer.observe(card));

    return () => {
      el.removeEventListener("scroll", measure);
      observer.disconnect();
    };
  }, [measure]);

  const scrollBehavior = () =>
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ? "auto"
      : "smooth";

  const step = (direction) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * stepSize(el), behavior: scrollBehavior() });
  };

  const goTo = (index) => {
    const el = trackRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    el.scrollTo({
      left: Math.min(max, index * stepSize(el)),
      behavior: scrollBehavior(),
    });
  };

  const handleKeyDown = (event) => {
    if (event.key === "ArrowRight") {
      event.preventDefault();
      step(1);
    } else if (event.key === "ArrowLeft") {
      event.preventDefault();
      step(-1);
    }
  };

  const pad = (n) => String(n).padStart(2, "0");

  return (
    <div className="svc-carousel">
      <div
        className="svc-carousel-track"
        ref={trackRef}
        role="region"
        aria-roledescription="carousel"
        aria-label={label}
        tabIndex={0}
        onKeyDown={handleKeyDown}
      >
        {children}
      </div>

      {scroll.overflows && (
        <div className="svc-carousel-controls">
          <div className="svc-carousel-dots">
            {Array.from({ length: scroll.stops }, (_, i) => (
              <button
                key={i}
                type="button"
                className={`svc-carousel-dot ${i === scroll.active ? "active" : ""}`}
                onClick={() => goTo(i)}
                aria-label={`Go to position ${i + 1} of ${scroll.stops}`}
                aria-current={i === scroll.active ? "true" : undefined}
              />
            ))}
            <span className="svc-carousel-count" aria-hidden="true">
              <strong>{pad(scroll.active + 1)}</strong> / {pad(scroll.stops)}
            </span>
          </div>

          <div className="svc-carousel-buttons">
            <button
              type="button"
              className="svc-carousel-btn"
              onClick={() => step(-1)}
              disabled={scroll.atStart}
              aria-label={`Previous ${label.toLowerCase()}`}
            >
              <ArrowIcon direction="prev" />
            </button>
            <button
              type="button"
              className="svc-carousel-btn"
              onClick={() => step(1)}
              disabled={scroll.atEnd}
              aria-label={`Next ${label.toLowerCase()}`}
            >
              <ArrowIcon direction="next" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ServiceCarousel;
